import React, { useState } from 'react';
import { Form as FinalForm } from 'react-final-form';
import classNames from 'classnames';

import { FormattedMessage, useIntl } from '../../../util/reactIntl';
import * as validators from '../../../util/validators';
import { Form, PrimaryButton, NamedLink } from '../../../components';

import AuthTextField from '../AuthTextField';
import emailIcon from '../../../assets/auth/login-email.svg';
import lockIcon from '../../../assets/auth/login-lock.svg';
import eyeOffIcon from '../../../assets/auth/login-eye.svg';
import arrowIcon from '../../../assets/auth/login-arrow.svg';

import fieldCss from '../authForm.module.css';
import css from './LoginForm.module.css';

const LoginFormComponent = props => {
  const [showPassword, setShowPassword] = useState(false);

  return (
    <FinalForm
      {...props}
      render={fieldRenderProps => {
        const {
          rootClassName,
          className,
          formId,
          handleSubmit,
          inProgress,
          intl,
          invalid,
          values,
          errors,
        } = fieldRenderProps;

        const emailLabel = intl.formatMessage({
          id: 'LoginForm.emailLabel',
        });
        const emailPlaceholder = intl.formatMessage({
          id: 'LoginForm.emailPlaceholder',
        });
        const emailRequiredMessage = intl.formatMessage({
          id: 'LoginForm.emailRequired',
        });
        const emailRequired = validators.required(emailRequiredMessage);
        const emailInvalidMessage = intl.formatMessage({
          id: 'LoginForm.emailInvalid',
        });
        const emailValid = validators.emailFormatValid(emailInvalidMessage);

        const passwordLabel = intl.formatMessage({
          id: 'LoginForm.passwordLabel',
        });
        const passwordPlaceholder = intl.formatMessage({
          id: 'LoginForm.passwordPlaceholder',
        });
        const passwordRequiredMessage = intl.formatMessage({
          id: 'LoginForm.passwordRequired',
        });
        const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
        const showPasswordLabel = intl.formatMessage({
          id: 'LoginForm.showPassword',
        });
        const hidePasswordLabel = intl.formatMessage({
          id: 'LoginForm.hidePassword',
        });

        const classes = classNames(rootClassName || css.root, className);
        const submitInProgress = inProgress;
        const submitDisabled = invalid || submitInProgress;
        const passwordType = showPassword ? 'text' : 'password';

        return (
          <Form className={classes} onSubmit={handleSubmit}>
            <div>
              <AuthTextField
                type="email"
                id={formId ? `${formId}.email` : 'email'}
                name="email"
                autoComplete="email"
                icon={emailIcon}
                label={emailLabel}
                placeholder={emailPlaceholder}
                validate={validators.composeValidators(emailRequired, emailValid)}
              />
              <AuthTextField
                type={passwordType}
                id={formId ? `${formId}.password` : 'password'}
                name="password"
                autoComplete="current-password"
                icon={lockIcon}
                label={passwordLabel}
                placeholder={passwordPlaceholder}
                validate={passwordRequired}
                endAdornment={
                  <button
                    type="button"
                    className={fieldCss.passwordToggle}
                    onClick={() => setShowPassword(visible => !visible)}
                    aria-label={showPassword ? hidePasswordLabel : showPasswordLabel}
                    aria-pressed={showPassword}
                  >
                    <img src={eyeOffIcon} alt="" width={16} height={16} />
                  </button>
                }
              />
              <p className={css.forgotPassword}>
                <NamedLink
                  name="PasswordRecoveryPage"
                  className={css.recoveryLink}
                  to={{
                    search:
                      values?.email && !errors?.email
                        ? `email=${encodeURIComponent(values.email)}`
                        : '',
                  }}
                >
                  <FormattedMessage id="LoginForm.forgotPassword" />
                </NamedLink>
              </p>
            </div>
            <PrimaryButton
              className={fieldCss.submitButton}
              type="submit"
              inProgress={submitInProgress}
              disabled={submitDisabled}
            >
              <span className={fieldCss.submitLabel}>
                <FormattedMessage id="LoginForm.logIn" />
              </span>
              {submitInProgress ? null : (
                <img
                  className={fieldCss.submitArrow}
                  src={arrowIcon}
                  alt=""
                  width={21}
                  height={7}
                />
              )}
            </PrimaryButton>
          </Form>
        );
      }}
    />
  );
};

/**
 * A component that renders the login form.
 *
 * @component
 * @param {Object} props
 * @param {string} props.rootClassName - The root class name that overrides the default class css.root
 * @param {string} props.className - The class that extends the root class
 * @param {string} props.formId - The form id
 * @param {boolean} props.inProgress - Whether the form is in progress
 * @returns {JSX.Element}
 */
const LoginForm = props => {
  const intl = useIntl();
  return <LoginFormComponent {...props} intl={intl} />;
};

export default LoginForm;
